import React from "react";
import { Heart, MessageCircle, Eye, Users, Star, Check } from "lucide-react";
import { Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { DiscoveryInfluencer } from "../../types/discovery.types";

export interface InfluencerCardProps {
  inf: DiscoveryInfluencer;
  onView: (handle: string) => void;
  isSelected: boolean;
  onToggleCompare: () => void;
  canAddMore: boolean;
}

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

export function InfluencerCard({
  inf,
  onView,
  isSelected,
  onToggleCompare,
  canAddMore,
}: InfluencerCardProps) {
  return (
    <div
      className={cn(
        "rounded-xl border bg-white flex flex-col p-5 mb-3 shadow-sm hover:shadow-md transition select-none",
        isSelected ? "border-purple-400 ring-2 ring-purple-100" : "border-gray-200"
      )}
    >
      {/* Profile picture + name + handle */}
      <div className="flex items-start gap-3 sm:gap-4 mb-3">
        <Avatar className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 flex-shrink-0">
          <AvatarImage src={inf.profilePic || undefined} alt={inf.name} className="object-cover" />
          <AvatarFallback className="bg-purple-100 text-purple-700 font-bold text-base sm:text-lg md:text-xl lg:text-2xl">
            {getInitials(inf.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0 pt-1 sm:pt-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-base text-gray-900 truncate">{inf.name}</span>
            {inf.verified && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] font-bold flex-shrink-0">
                ✓
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <a
              href={`https://www.instagram.com/${inf.handle.replace(/^@/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:underline hover:text-pink-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {inf.handle}
            </a>
            <span>·</span>
            <span className="truncate">{inf.location}</span>
          </div>
        </div>
      </div>

      {/* Platforms + stats */}
      <div className="flex flex-wrap gap-2 mb-3">
        {inf.platforms.includes("instagram") && inf.followers.instagram && (
          <span className="flex items-center gap-1 text-xs bg-pink-50 text-pink-600 rounded-full px-2 py-0.5 font-semibold">
            <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-3 h-3" /> {inf.followers.instagram}
          </span>
        )}
        {inf.platforms.includes("youtube") && inf.followers.youtube && (
          <span className="flex items-center gap-1 text-xs bg-red-50 text-red-700 rounded-full px-2 py-0.5 font-semibold">
            <Youtube className="w-3 h-3" /> {inf.followers.youtube}
          </span>
        )}
        {inf.engagement && (
          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
            <svg className="inline w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M4 12l6 6L20 6" />
            </svg>
            {inf.engagement}
          </span>
        )}
        {inf.tier && (
          <span className="bg-blue-50 text-blue-800 px-2 py-0.5 text-xs rounded-full font-medium">
            {inf.tier}
          </span>
        )}
        {inf.qualityScore != null && inf.qualityScore > 0 && (
          <span className="flex items-center gap-0.5 text-xs text-amber-600 font-medium">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            {inf.qualityScore.toFixed(1)}
          </span>
        )}
      </div>

      {/* Performance metrics */}
      {(inf.avgLikes || inf.avgComments || inf.avgViews || inf.estimatedReach) && (
        <div className="flex flex-wrap gap-3 mb-3 py-2 px-3 bg-gray-50 rounded-lg">
          {inf.avgLikes && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Heart className="w-3.5 h-3.5 text-pink-500" />
              <span className="font-medium">{inf.avgLikes}</span>
              <span className="text-gray-400">avg</span>
            </div>
          )}
          {inf.avgComments && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <MessageCircle className="w-3.5 h-3.5 text-blue-500" />
              <span className="font-medium">{inf.avgComments}</span>
              <span className="text-gray-400">avg</span>
            </div>
          )}
          {inf.avgViews && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Eye className="w-3.5 h-3.5 text-purple-500" />
              <span className="font-medium">{inf.avgViews}</span>
              <span className="text-gray-400">views</span>
            </div>
          )}
          {inf.estimatedReach && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Users className="w-3.5 h-3.5 text-green-500" />
              <span className="font-medium">{inf.estimatedReach}</span>
              <span className="text-gray-400">reach</span>
            </div>
          )}
        </div>
      )}

      {/* Categories + languages */}
      {(inf.languages.length > 0 || inf.categories.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {inf.languages.map((lng: string, i: number) => (
            <span key={`lng${i}`} className="bg-gray-50 text-gray-700 px-2 py-0.5 rounded text-xs border border-gray-200">
              {lng}
            </span>
          ))}
          {inf.categories.map((cat: string, i: number) => (
            <span key={`cat${i}`} className="bg-blue-50 text-blue-800 px-2 py-0.5 rounded text-xs border border-blue-200">
              #{cat}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 items-center mt-auto">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                size="sm"
                variant={isSelected ? "secondary" : "outline"}
                onClick={onToggleCompare}
                disabled={!isSelected && !canAddMore}
                className={cn(isSelected && "bg-purple-100 text-purple-700 hover:bg-purple-200")}
              >
                {isSelected ? (
                  <>
                    <Check className="w-3 h-3 mr-1" />
                    Added
                  </>
                ) : (
                  "Compare"
                )}
              </Button>
            </span>
          </TooltipTrigger>
          {!isSelected && !canAddMore && (
            <TooltipContent>You can compare up to 4 influencers</TooltipContent>
          )}
        </Tooltip>
        <Button size="sm" variant="outline" type="button">Save</Button>
        <Button size="sm" variant="default" type="button" onClick={() => onView(inf.handle)}>View</Button>
      </div>
    </div>
  );
}
